import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/auth/session?check=${Date.now()}`)
      .then((res) => setUser(res.data.authenticated ? res.data.user : null))
      .catch((error) => {
        console.error("Unable to restore the secure session", error);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const persist = useCallback((data) => {
    setUser(data.user);
    return data.user;
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    return persist(res.data);
  }, [persist]);

  const signup = useCallback(async (payload) => {
    const res = await api.post("/auth/register", payload);
    return persist(res.data);
  }, [persist]);

  const logout = useCallback(async () => {
    setUser(null);
    try { await api.post("/auth/logout"); } catch { /* Local access is already cleared if the network is unavailable. */ }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout }),
    [user, loading, login, signup, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
